import React, { FormEvent, useEffect, useState } from 'react';
import {
  IonRow,
  IonCol,
  IonGrid,
  IonPage,
  IonContent,
  IonInput,
  IonButton
} from '@ionic/react';
import { connect } from 'react-redux';
import { AppointmentStepProps } from '../navigators/AppointmentNavigator';
import AppHeader from './AppHeader';
import Stepper from './Stepper';
import { QcmQuestion } from './QcmQuestion';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch
} from 'react-router';
import { resetAppointment, setSidep } from '../store/actions/appointments';
import { AppointmentsRootState } from '../store/reducers/appointments';
import Select from 'react-select';
import { frenchDeparments } from '../utils/frenchDepartments';
import './SidepForm.scss';
import { AuthenticationRootState } from '../store/reducers/authentication';
import PatientRepository from '../repositories/PatientRepository';
import { PatientToUpdate } from '../models/Patient';
import EventBus from '../services/EventBus';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import CountrySelect from './CountrySelect';

type SidepFormProps = {
  isSidepOnly?: boolean;
};

const mapStateToProps = (
  state: {
    authentication: AuthenticationRootState;
    appointments: AppointmentsRootState;
  },
  ownProps: any
) => {
  return {
    sidep: state.appointments.sidep,
    isLoggedIn: !!state.authentication.accessToken,
    patientToUpdate:
      state.appointments.appointment.patient ||
      state.authentication.user?.patient,
    currentAppointment: state.appointments.appointment
  };
};

const mapDispatchToProps = (dispatch: Function, ownProps: any) => ({
  setSidep: ({ key, value }: { key: string; value: unknown }) =>
    dispatch(setSidep(key, value)),
  resetAppointment: () => dispatch(resetAppointment())
});

const SidepForm: React.FC<
  ReturnType<typeof mapDispatchToProps> &
    SidepFormProps &
    AppointmentStepProps &
    ReturnType<typeof mapStateToProps> &
    AppointmentStepProps
> = ({
  sidep,
  step,
  nextStepRouterLink = '/',
  setSidep,
  resetAppointment,
  patientToUpdate,
  isSidepOnly = false,
  isLoggedIn,
  currentAppointment
}) => {
  const [valueToSave, setValueToSave] = useState<any>();
  const match = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const routes = [
    'symptoms',
    'cpam',
    'professionnel',
    'residence',
    'residence-principale',
    'pays-actuel',
    'departement-actuel',
    'retour-voyage',
    'pays',
    'voyage',
    'document-identite-type',
    'document-identite-numero',
    'consent'
  ];
  let timeout: number;

  useEffect(() => {
    const urlSplit = history.location.pathname.replace(/\/$/, '').split('/');
    let currentStep = urlSplit[urlSplit.length - 1];
    let currentStepIndex = routes.findIndex((route) => route === currentStep);

    if (isSidepOnly && currentStepIndex <= 0) {
      resetAppointment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [departments] = useState(
    frenchDeparments.map(({ code, name }) => ({
      value: code,
      label: name
    }))
  );

  function onFormSubmit(e: FormEvent) {
    e.preventDefault();
    submit(valueToSave);
  }

  function submit(value: any, _key?: string) {
    if (value === undefined) {
      return;
    }
    const urlSplit = history.location.pathname.replace(/\/$/, '').split('/');
    let currentStep = urlSplit[urlSplit.length - 1];
    let currentStepIndex = routes.findIndex((route) => route === currentStep);

    if (currentStepIndex === -1) {
      currentStepIndex = 0;
      currentStep = routes[currentStepIndex];
    }

    let nextStepOffset = 1;

    if (currentStep === 'residence-principale' && value === 'oui') {
      nextStepOffset = 3;
    }

    if (currentStep === 'pays-actuel' && value.value !== 'FR') {
      nextStepOffset = 2;
    }

    if (currentStep === 'retour-voyage' && value === 'non') {
      nextStepOffset = 2;
    }

    if (currentStep === 'voyage' && value === 'non') {
      nextStepOffset = 3;
    }

    const nextStep = routes[currentStepIndex + nextStepOffset];

    if (currentStep === 'retour-voyage' && value === 'non') {
      setSidep({ key: 'pays', value: undefined });
    } else if (currentStep === 'voyage') {
      setSidep({ key: 'voyage', value: value === 'oui' ? {} : undefined });
    } else if (currentStep === 'document-identite-type') {
      setSidep({
        key: 'voyage',
        value: {
          ...sidep.voyage,
          type: value.value
        }
      });
    } else if (currentStep === 'document-identite-numero') {
      setSidep({
        key: 'voyage',
        value: {
          ...sidep.voyage,
          numero: value
        }
      });

      currentStep = 'voyage';
      value = {
        ...sidep.voyage,
        numero: value
      };
    } else {
      setSidep({ key: currentStep, value });
    }

    setValueToSave(undefined);

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = window.setTimeout(() => {
      if (typeof nextStep !== 'undefined') {
        history.push(`${match.url.replace(/\/$/, '')}/${nextStep}`);
      } else {
        if (isLoggedIn && patientToUpdate) {
          PatientRepository.update(
            new PatientToUpdate({
              id: patientToUpdate.id,
              sidep: {
                ...sidep,
                [currentStep]: value
              }
            })
          )
            .then(() => {
              history.push(nextStepRouterLink);
            })
            .catch((e) => {
              EventBus.error(
                'Une erreur est survenue. Veuillez essayer à nouveau.'
              );
              history.push(`${match.url.replace(/\/$/, '')}`);
            });
        } else {
          history.push(nextStepRouterLink);
        }
      }
    }, 600);
  }

  function onQuestionChange(value: string) {
    setValueToSave(value);
    submit(value);
  }

  return (
    <IonPage>
      <AppHeader />
      <Stepper step={step} />
      <IonContent>
        <form onSubmit={onFormSubmit}>
          <IonGrid>
            <IonRow>
              <IonCol>
                <div
                  className="wrapper wrapper--large ion-margin-start ion-margin-end"
                  style={{ position: 'relative' }}
                >
                  <TransitionGroup>
                    <CSSTransition
                      key={location.key}
                      timeout={{ enter: 700, exit: 300 }}
                      classNames={'fade'}
                    >
                      <Switch location={location}>
                        <Route exact path={`${match.url}`}>
                          <QcmQuestion
                            onChange={onQuestionChange}
                            key="symptoms"
                            title="J’ai des symptômes (perte de l’odorat, perte du goût, fièvre, toux...) et ils sont apparus :"
                            options={[
                              {
                                label: "Je n'ai pas de symptôme",
                                name: 'asymptomatic'
                              },
                              {
                                label: 'Moins de 24h avant le prélèvement',
                                name: 'j+0'
                              },
                              {
                                label: '2, 3 ou 4 jours avant le prélèvement',
                                name: 'j+2'
                              },
                              {
                                label: '5, 6 ou 7 jours avant le prélèvement',
                                name: 'j+5'
                              },
                              {
                                label:
                                  'Entre 8 et 14 jours avant le prélèvement',
                                name: 'j+8'
                              },
                              {
                                label:
                                  'Entre 15 et 28 jours avant le prélèvement',
                                name: 'j+15'
                              },
                              {
                                label:
                                  'Plus quatre semaines avant le prélèvement',
                                name: 'j+28'
                              }
                            ]}
                            defaultValue={sidep.symptoms}
                          />
                        </Route>
                        <Route exact path={`${match.url}/professionnel`}>
                          <QcmQuestion
                            onChange={onQuestionChange}
                            key="professionnel"
                            title="Je suis un professionnel intervenant dans le système de santé"
                            defaultValue={sidep.professionnel}
                          />
                        </Route>
                        <Route exact path={`${match.url}/cpam`}>
                          <QcmQuestion
                            onChange={onQuestionChange}
                            key="cpam"
                            title="Avez-vous été contacté par la CPAM (en tant que cas contact) ?"
                            defaultValue={sidep.cpam}
                          />
                        </Route>
                        <Route path={`${match.url}/residence`}>
                          <QcmQuestion
                            onChange={onQuestionChange}
                            key="residence"
                            title="Mon lieu de résidence est..."
                            options={[
                              {
                                name: 'Hébergement individuel privé'
                              },
                              {
                                name: 'Hospitalisé au moment du prélèvement'
                              },
                              {
                                name: 'Résident en EHPAD'
                              },
                              {
                                name: 'En milieu carcéral'
                              },
                              {
                                name: "Autre structure d'hébergement collectif"
                              },
                              {
                                name: 'Je ne sais pas'
                              }
                            ]}
                            defaultValue={sidep.residence}
                          />
                        </Route>
                        <Route exact path={`${match.url}/residence-principale`}>
                          <QcmQuestion
                            onChange={onQuestionChange}
                            key="residence-cv"
                            title="​Je vis actuellement dans ma résidence principale"
                            defaultValue={sidep['residence-principale']}
                          />
                        </Route>
                        <Route exact path={`${match.url}/pays-actuel`}>
                          <div className="qcm">
                            <h2 className="qcm__title">
                              Je vis actuellement en...
                            </h2>
                            <div className="qcm__questions">
                              <CountrySelect
                                defaultValue={sidep.paysActuel}
                                onChange={(value) =>
                                  value ? onQuestionChange(value) : null
                                }
                                placeholder="Sélectionner..."
                                className="ion-margin-bottom"
                              />
                            </div>
                          </div>
                        </Route>
                        <Route exact path={`${match.url}/departement-actuel`}>
                          <div className="qcm">
                            <h2 className="qcm__title">
                              Je vis actuellement dans ce département...
                            </h2>
                            <div className="qcm__questions">
                              <Select
                                defaultValue={sidep['departement-actuel']}
                                onChange={(value) =>
                                  value ? onQuestionChange(value) : null
                                }
                                options={departments}
                                placeholder="Sélectionner..."
                                isClearable
                                className="ion-margin-bottom"
                                classNamePrefix="sidep-select"
                              />
                            </div>
                          </div>
                        </Route>
                        <Route exact path={`${match.url}/retour-voyage`}>
                          <QcmQuestion
                            onChange={onQuestionChange}
                            key="retourVoyage"
                            title="Je reviens d'un voyage dans un pays étranger"
                            defaultValue={sidep['retour-voyage']}
                          />
                        </Route>
                        <Route exact path={`${match.url}/pays`}>
                          <div className="qcm">
                            <h2 className="qcm__title">
                              Quel était le pays de départ ?
                            </h2>
                            <div className="qcm__questions">
                              <CountrySelect
                                defaultValue={sidep.pays}
                                onChange={(value) =>
                                  value ? onQuestionChange(value) : null
                                }
                                placeholder="Sélectionner..."
                                className="ion-margin-bottom"
                              />
                            </div>
                          </div>
                        </Route>
                        <Route exact path={`${match.url}/voyage`}>
                          <QcmQuestion
                            onChange={onQuestionChange}
                            key="voyage"
                            title="Est-ce que ce test COVID est à but de voyage ?"
                          />
                        </Route>
                        <Route
                          exact
                          path={`${match.url}/document-identite-type`}
                        >
                          <div className="qcm">
                            <h2 className="qcm__title">
                              Votre document d'identité
                            </h2>
                            <h2 className="qcm__title qcm__title--small">
                              Type de document
                            </h2>
                            <div className="qcm__questions">
                              <Select
                                defaultValue={sidep.voyage?.type as any}
                                onChange={(value) =>
                                  value ? onQuestionChange(value) : null
                                }
                                options={[
                                  {
                                    value: "Carte nationale d'identité",
                                    label: "Carte nationale d'identité"
                                  },
                                  {
                                    value: 'Passeport',
                                    label: 'Passeport'
                                  }
                                ]}
                                placeholder="Sélectionner..."
                                isClearable
                                className="ion-margin-bottom"
                                classNamePrefix="sidep-select"
                              />
                            </div>
                          </div>
                        </Route>
                        <Route
                          exact
                          path={`${match.url}/document-identite-numero`}
                        >
                          <div className="qcm">
                            <h2 className="qcm__title">
                              Votre document d'identité
                            </h2>
                            <h2 className="qcm__title qcm__title--small ion-margin-top">
                              Numéro de la carte d'identité ou du passeport
                            </h2>
                            <div className="qcm__questions">
                              <div>
                                <IonInput
                                  defaultValue={sidep.voyage?.numero}
                                  autofocus
                                  placeholder="123..."
                                  onIonChange={(e) => {
                                    setValueToSave(e.detail.value!);
                                  }}
                                  onKeyPress={(e) => {
                                    e.key === 'Enter' && e.preventDefault();
                                  }}
                                />
                                <div className="ion-margin-top ion-text-center">
                                  <IonButton
                                    type="submit"
                                    disabled={!valueToSave}
                                  >
                                    Envoyer
                                  </IonButton>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Route>
                        <Route exact path={`${match.url}/consent`}>
                          <QcmQuestion
                            onChange={onQuestionChange}
                            key="consent"
                            title="J’accepte la transmission de mes données non anonymisées au SI-DEP (Système d’information et de dépistage)"
                            info={
                              <>
                                En cas de refus, vous recevrez votre compte
                                rendu du résultat du laboratoire mais{' '}
                                <strong>
                                  pas de Certificat de test (QR code) et/ou de
                                  Certificat de rétablissement
                                </strong>
                                .
                              </>
                            }
                            defaultValue={sidep.consent}
                          />
                        </Route>
                      </Switch>
                    </CSSTransition>
                  </TransitionGroup>
                </div>
              </IonCol>
            </IonRow>
          </IonGrid>
        </form>
      </IonContent>
    </IonPage>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(SidepForm);
